import { Badge, Box, Button, Dialog, Divider, Group, Paper, Stack, Text } from '@mantine/core'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'

import { CurrencyField } from '@/components/commons/DisplayData'
import { PictureCard } from '@/components/surfaces'
import { useBasket } from '@/providers/BasketProvider'
import {
  Product as ProductType,
  ProductLine as ProductLineType,
  Showcase as ShowcaseType,
  Student as StudentType
} from '@/types'

export default function ProductAddToBasketDialog() {
  // Hooks
  const {
    basket,
    openAddedProductModal: product,
    setOpenAddedProductModal,
    setOpenBasketDrawer
  } = useBasket()

  // Constants
  const {
    discountTotal,
    enrollments,
    freightTotal,
    lines = [],
    numLines,
    products = [],
    showcases = [],
    students = [],
    subtotal,
    total
  } = basket || {}
  const summaryData = useMemo(() => {
    return [
      { label: 'ITENS', value: numLines },
      { label: 'SUB-TOTAL', value: subtotal },
      { label: 'FRETE', value: freightTotal, hidden: numLines === 0 },
      { label: 'DESCONTO', value: discountTotal },
      { label: 'TOTAL', value: total }
    ]
  }, [discountTotal, freightTotal, numLines, subtotal, total])

  const enrollmentBaskets = useMemo(() => {
    return enrollments?.map(
      ({ student, showcase, uid }: { student: string; showcase: string; uid: string }) => ({
        uid,
        student: students?.find((item: StudentType) => item.uid === student),
        showcase: showcases?.find((item: ShowcaseType) => item.uid === showcase),
        lines: lines?.filter((item: ProductLineType) => item.enrollment === uid)
      })
    )
  }, [enrollments, lines, showcases, students])

  const renderBasketLines = useMemo(() => {
    return (
      <Stack spacing="sm">
        {enrollmentBaskets?.map(
          ({
            student,
            lines,
            uid
          }: {
            student: StudentType
            lines: ProductLineType[]
            uid: string
          }) => {
            const studentFullName = student ? `${student.firstName} ${student.lastName}` : ''
            return (
              <Paper key={uid} p="sm" withBorder>
                <Stack>
                  {student && (
                    <PictureCard
                      title={studentFullName}
                      subtitle="comprando para"
                      picture={student.picture}
                      subtitlePosition="before"
                    />
                  )}
                  <Divider />
                  {lines?.map(({ product: productUid, quantity, uid }) => {
                    const lineProduct = products.find(
                      (item: ProductType) => item.uid === productUid
                    )
                    return lineProduct ? (
                      <Group position="apart" noWrap key={uid}>
                        <PictureCard
                          title={lineProduct.title}
                          subtitle={
                            lineProduct?.description ||
                            (lineProduct?.upc ? `COD. ${lineProduct?.upc}` : '')
                          }
                          picture={lineProduct?.image?.file}
                          pictureRadius="sm"
                          subtitlePosition="before"
                        />
                        <Badge size="sm">qtd. {quantity}</Badge>
                      </Group>
                    ) : null
                  })}
                </Stack>
              </Paper>
            )
          }
        )}
      </Stack>
    )
  }, [enrollmentBaskets, products])

  const renderAddedProduct = useMemo(() => {
    return (
      <Paper withBorder maw={400} style={{ overflow: 'hidden' }}>
        <Group spacing={0} noWrap sx={{ alignItems: 'flex-start', justifyContent: 'flex-start' }}>
          <Box sx={{ position: 'relative', width: 100, height: 100, flexShrink: 0 }}>
            {product?.primaryImage?.file || product?.image?.file || product?.images?.[0]?.file ? (
              <Image
                src={
                  product?.primaryImage?.file ||
                  product?.image?.file ||
                  product?.images?.[0]?.file ||
                  ''
                }
                alt={product?.title || ''}
                fill
                style={{ objectFit: 'cover' }}
              />
            ) : null}
          </Box>

          <Box p="sm">
            <Text size="sm" weight="bold">
              {product?.title}
            </Text>
            {!!product?.seller?.displayName && (
              <Text size="sm" color="dimmed" mb="xs">
                Vendido por{' '}
                <Badge
                  radius="xl"
                  leftSection={
                    product?.seller?.logo ? (
                      <Image
                        src={product.seller.logo}
                        alt={`Vendido por ${product.seller.displayName}`}
                        quality={60}
                        width={20}
                        height={20}
                      />
                    ) : undefined
                  }
                  sx={{ paddingLeft: 0 }}>
                  {product.seller.displayName}
                </Badge>
              </Text>
            )}
            <Text size="sm" color="dimmed">
              {product?.description}
            </Text>
          </Box>
        </Group>
      </Paper>
    )
  }, [product])

  if (!product) return null

  return (
    <Dialog
      size="auto"
      withCloseButton
      position={{ top: 10, right: 10 }}
      mah="calc(100vh - 20px)"
      opened={!!product}
      onClose={() => setOpenAddedProductModal(null)}>
      <Stack>
        <Text weight="bold">Produto adicionado no carrinho</Text>
        <Box style={{ height: '100%', maxHeight: 'calc(100vh - 390px)', overflow: 'auto' }}>
          <Stack>
            {renderAddedProduct}
            {renderBasketLines}
          </Stack>
        </Box>

        <Paper p="sm" withBorder>
          <Stack spacing="xs">
            {summaryData
              .filter(({ hidden }) => !hidden)
              .map(({ label, value }) => (
                <Group position="apart" key={label}>
                  <Text color="dimmed" size="xs">
                    {label}
                  </Text>
                  <Text size="sm">
                    {label === 'ITENS' ? <Badge>{value}</Badge> : <CurrencyField value={value} />}
                  </Text>
                </Group>
              ))}
          </Stack>
        </Paper>
        {basket?.uid && (
          <Button
            component={Link}
            color="green"
            href={`/checkout/${basket?.uid}`}
            disabled={Array.isArray(lines) && lines.length === 0}
            onClick={() => setOpenBasketDrawer?.(false)}>
            Ir para checkout
          </Button>
        )}
        <Button
          onClick={() => {
            setOpenBasketDrawer?.(true)
            setOpenAddedProductModal(null)
          }}>
          Ver carrinho
        </Button>
      </Stack>
    </Dialog>
  )
}
